import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSite } from "../lib/SiteContext";
import api from "../lib/api";
import { fmt, fmtInt, relativeTime, shortDate } from "../lib/format";
import { deriveDeviceStatus, deviceSource, isBatteryGroupDevice, providerLabel } from "../lib/deviceStatus";
import { deviceLastSeen, deviceReadingsCount } from "../lib/deviceMetadata";
import HomeWizardBatteryGroupDetail, {
  MetricTile, pickLatest,
} from "../components/devices/HomeWizardBatteryGroupDetail";
import {
  Activity, ArrowDownToLine, ArrowUpFromLine, Cpu, Gauge, Plug, Zap,
} from "lucide-react";

const DEVICES_POLL_MS = 30_000;

// HomeWizard P1 metrics we surface in the detail panel.
const HW_P1_METRICS = [
  { key: "active_power_w", label: "Active power", unit: "W", accent: "#F59E0B", Icon: Activity },
  { key: "total_power_import_kwh", label: "Total import", unit: "kWh", accent: "#E11D48", Icon: ArrowDownToLine },
  { key: "total_power_export_kwh", label: "Total export", unit: "kWh", accent: "#10B981", Icon: ArrowUpFromLine },
  { key: "active_voltage_v", label: "Voltage", unit: "V", accent: "#60A5FA", Icon: Zap },
  { key: "active_current_a", label: "Current", unit: "A", accent: "#3B82F6", Icon: Gauge },
];

function StatusPill({ status, testid }) {
  return (
    <span
      className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded"
      style={{ color: status.accent, backgroundColor: `${status.accent}15`, border: `1px solid ${status.accent}30` }}
      data-testid={testid}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: status.accent }} />
      {status.label}
    </span>
  );
}

function DeviceCard({ device, isActive, onClick }) {
  const lastSeen = deviceLastSeen(device);
  const status = deriveDeviceStatus(lastSeen);
  return (
    <button
      onClick={onClick}
      data-testid={`device-card-${device.external_id || device.id}`}
      className={`text-left card-base p-4 transition-colors hover:bg-secondary/30 ${
        isActive ? "ring-1 ring-foreground/60" : ""
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-md bg-secondary grid place-items-center">
            <Plug className="w-4 h-4 text-foreground" />
          </div>
          <div>
            <div className="font-medium text-sm leading-tight">{device.name || device.external_id || "Unnamed device"}</div>
            <div className="text-[11px] text-muted-foreground mt-0.5">
              {providerLabel(device.provider)} · {device.domain || "device"}
            </div>
          </div>
        </div>
        <StatusPill status={status} testid={`device-status-${device.external_id || device.id}`} />
      </div>
      <div className="grid grid-cols-2 gap-2 mt-4 text-[11px]">
        <div>
          <div className="label-tiny">Last seen</div>
          <div className="text-muted-foreground mt-0.5">{relativeTime(lastSeen)}</div>
        </div>
        <div>
          <div className="label-tiny">Readings</div>
          <div className="text-muted-foreground mt-0.5 tabular-nums">{fmtInt(deviceReadingsCount(device))}</div>
        </div>
        <div className="col-span-2">
          <div className="label-tiny">Source</div>
          <div className="text-muted-foreground mt-0.5">{deviceSource(device.provider)}</div>
        </div>
      </div>
    </button>
  );
}

function MetricTileLocal() { return null; /* deprecated — see ../components/devices/HomeWizardBatteryGroupDetail */ }

function HomeWizardDetail({ readings, peakPowerW }) {
  const tiles = HW_P1_METRICS.map(({ key, label, unit, accent, Icon }) => {
    const latest = pickLatest(readings, key);
    return (
      <MetricTile
        key={key}
        Icon={Icon}
        label={label}
        value={latest ? fmt(latest.value, key.endsWith("_kwh") ? 2 : 1) : "—"}
        unit={unit}
        accent={accent}
      />
    );
  });

  return (
    <div data-testid="device-detail-homewizard">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5">{tiles}</div>
      {peakPowerW != null && (
        <div className="mt-4 text-[11px] text-muted-foreground" data-testid="device-detail-peak">
          Peak today (from energy aggregator): <span className="font-mono text-foreground">{fmtInt(peakPowerW)} W</span>
        </div>
      )}
    </div>
  );
}


// HomeWizard v2 Battery Group detail moved to
// /app/frontend/src/components/devices/HomeWizardBatteryGroupDetail.jsx
// so the dedicated Battery page can reuse the exact same component.

function ReadingsTable({ readings }) {
  if (!readings.length) {
    return (
      <div className="text-xs text-muted-foreground py-6 text-center" data-testid="device-readings-empty">
        No readings yet for this device.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="device-readings-table">
        <thead className="text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border">
          <tr>
            <th className="text-left px-5 py-2.5">Timestamp</th>
            <th className="text-left px-3 py-2.5">Metric</th>
            <th className="text-right px-3 py-2.5">Value</th>
            <th className="text-right px-5 py-2.5">Unit</th>
          </tr>
        </thead>
        <tbody className="font-mono text-[12px]">
          {readings.slice(0, 20).map((r) => (
            <tr key={r.id} className="border-b border-border/60 hover:bg-secondary/30">
              <td className="px-5 py-2 font-sans">{shortDate(r.timestamp)} <span className="text-muted-foreground">{new Date(r.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}</span></td>
              <td className="px-3">{r.metric}</td>
              <td className="text-right px-3 tabular-nums">{fmt(r.value, 3)}</td>
              <td className="text-right px-5 text-muted-foreground">{r.unit || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BridgeSyncStrip({ syncRuns, deviceId }) {
  const deviceRuns = syncRuns.filter((r) => (r.summary_json || {}).device_id === deviceId);
  if (!deviceRuns.length) return null;
  const latest = deviceRuns[0];
  return (
    <div className="text-[11px] text-muted-foreground flex flex-wrap gap-3" data-testid="device-bridge-sync">
      <span>Last bridge sync: <span className="text-foreground">{relativeTime(latest.completed_at || latest.started_at)}</span></span>
      <span>Status: <span className="text-foreground">{latest.status}</span></span>
      <span>Readings ingested last batch: <span className="text-foreground">{latest.rows_created}</span></span>
    </div>
  );
}

export default function Devices() {
  const { currentSite } = useSite();
  const [devices, setDevices] = useState([]);
  const [readings, setReadings] = useState([]);
  const [syncRuns, setSyncRuns] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const reloadDevices = useCallback(async () => {
    if (!currentSite) return;
    try {
      const [devRes, runsRes] = await Promise.all([
        api.get(`/sites/${currentSite.id}/devices`),
        api.get(`/sites/${currentSite.id}/sync-runs?limit=25`),
      ]);
      setDevices(devRes.data || []);
      setSyncRuns(runsRes.data || []);
      setLoading(false);
      setErr(null);
    } catch (e) {
      setErr(e.userMessage || "Could not load devices");
      setLoading(false);
    }
  }, [currentSite]);

  const reloadReadings = useCallback(async () => {
    if (!currentSite || !selectedId) return;
    try {
      const { data } = await api.get(
        `/sites/${currentSite.id}/devices/${selectedId}/readings?limit=100`,
      );
      setReadings(data || []);
    } catch (e) {
      setErr(e.userMessage || "Could not load readings");
    }
  }, [currentSite, selectedId]);

  // Pick a default-selected device once we have one.
  useEffect(() => {
    if (!selectedId && devices.length > 0) setSelectedId(devices[0].id);
  }, [devices, selectedId]);

  // Initial load + 30s polling for the device list (status freshness).
  useEffect(() => {
    reloadDevices();
    const t = setInterval(reloadDevices, DEVICES_POLL_MS);
    return () => clearInterval(t);
  }, [reloadDevices]);

  // Refresh readings whenever the selection changes; also poll every 30s
  // so the readings table stays close-to-real-time for the active device.
  useEffect(() => {
    reloadReadings();
    if (!selectedId) return undefined;
    const t = setInterval(reloadReadings, DEVICES_POLL_MS);
    return () => clearInterval(t);
  }, [reloadReadings, selectedId]);

  const selected = useMemo(
    () => devices.find((d) => d.id === selectedId) || null,
    [devices, selectedId],
  );
  const selectedStatus = useMemo(
    () => (selected ? deriveDeviceStatus(deviceLastSeen(selected)) : null),
    [selected],
  );
  // Peak power (today) lives in the latest sync_runs.summary_json for HomeWizard,
  // surfaced by the energy aggregator. Pull the most recent value if present.
  const peakPowerW = useMemo(() => {
    if (!selected) return null;
    for (const run of syncRuns) {
      if ((run.summary_json || {}).device_id !== selected.id) continue;
      const der = run.summary_json?.energy_derivation;
      if (Array.isArray(der)) {
        for (const d of der) {
          if (d?.derived?.peak_power_w != null) return d.derived.peak_power_w;
        }
      }
    }
    return null;
  }, [syncRuns, selected]);

  const isHomeWizardBattery = isBatteryGroupDevice(selected);
  const isHomeWizard = selected && selected.provider === "homewizard" && !isHomeWizardBattery;

  return (
    <div className="space-y-8 pb-12" data-testid="devices-page">
      <header>
        <div className="label-tiny mb-1.5 flex items-center gap-2">
          <Cpu className="w-3.5 h-3.5" /> Devices
        </div>
        <h1 className="font-heading text-3xl font-semibold">Connected devices</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Live device telemetry pushed by your local bridge. Status, last readings, and a one-line audit trail.
        </p>
      </header>

      {err && <div className="text-xs text-[#EF4444]" data-testid="devices-error">{err}</div>}

      {loading ? (
        <div className="text-sm text-muted-foreground" data-testid="devices-loading">Loading devices…</div>
      ) : devices.length === 0 ? (
        <section className="card-base p-10 text-center max-w-xl" data-testid="devices-empty">
          <div className="w-12 h-12 mx-auto rounded-xl bg-secondary grid place-items-center">
            <Plug className="w-6 h-6 text-muted-foreground" />
          </div>
          <div className="font-heading text-lg font-semibold mt-4">No devices yet</div>
          <p className="text-sm text-muted-foreground mt-2">
            Connect a local bridge to start receiving device readings.
            See Settings → Bridge token for the per-site token and the
            <span className="font-mono"> POST /api/sites/{`{site_id}`}/ingest </span>
            endpoint expected by <span className="font-mono">/bridge/dh_bridge.py</span>.
          </p>
        </section>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          {/* Device list */}
          <aside className="lg:col-span-4 xl:col-span-3 space-y-3" data-testid="devices-list">
            {devices.map((d) => (
              <DeviceCard
                key={d.id}
                device={d}
                isActive={d.id === selectedId}
                onClick={() => setSelectedId(d.id)}
              />
            ))}
          </aside>

          {/* Detail */}
          <section className="lg:col-span-8 xl:col-span-9 space-y-5" data-testid="device-detail">
            {selected && (
              <>
                <div className="card-base p-5">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <div className="text-[11px] text-muted-foreground uppercase tracking-wider">
                        {providerLabel(selected.provider)}
                      </div>
                      <h2 className="font-heading text-xl font-semibold mt-1">
                        {selected.name || selected.external_id}
                      </h2>
                      <div className="text-[11px] text-muted-foreground mt-1">
                        external id <span className="font-mono">{selected.external_id}</span>
                        {" · "}domain {selected.domain || "device"}
                        {" · "}{fmtInt(deviceReadingsCount(selected))} readings stored
                      </div>
                    </div>
                    {selectedStatus && <StatusPill status={selectedStatus} testid="device-detail-status" />}
                  </div>

                  <div className="mt-5">
                    {isHomeWizard ? (
                      <HomeWizardDetail readings={readings} peakPowerW={peakPowerW} />
                    ) : isHomeWizardBattery ? (
                      <HomeWizardBatteryGroupDetail readings={readings} />
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        No provider-specific dashboard yet — see the readings timeline below.
                      </p>
                    )}
                  </div>

                  <div className="mt-4 pt-4 border-t border-border">
                    <BridgeSyncStrip syncRuns={syncRuns} deviceId={selected.id} />
                  </div>
                </div>

                <div className="card-base overflow-hidden">
                  <div className="px-5 py-3 border-b border-border flex items-center justify-between">
                    <div className="label-tiny">Latest readings · last {Math.min(20, readings.length)}</div>
                    <div className="text-[11px] text-muted-foreground">auto-refresh every 30 s</div>
                  </div>
                  <ReadingsTable readings={readings} />
                </div>
              </>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
